// The ledger (GDD §15.1-5): every settle itemised — earned, interest, cost of
// living, Living, tuition, cards. "Spend-vs-bank is the game; the ledger is
// the game board." Lifetime + this-year columns, and the §6 identity row that
// reproduces the balance from the lines shown (UX-6).

import type { Params } from '@lifespan/engine';
import {
  ledgerIdentity,
  lifetimeLedger,
  thisYearLedger,
  type LedgerLines,
  type Session,
} from '../game/driver.ts';
import { money, moneyDelta } from '../game/format.ts';

// sign 1/-1: strictly one-directional accumulators, printed with a fixed sign.
// sign 0: NET accumulators — the printed sign comes from the value itself,
// never from config. Cards can go negative under spend cards (M7); Interest
// is net of overdraft interest PAID and goes genuinely negative for a life
// that dies deep in debt (found by Trevor's first ruinous playtest: net
// −$8,813 rendered as "+$8,813" while the identity used the true negative).
const ROWS: { key: keyof LedgerLines; label: string; sign: 1 | -1 | 0 }[] = [
  { key: 'earned', label: 'Earned', sign: 1 },
  { key: 'interest', label: 'Interest', sign: 0 },
  { key: 'costOfLiving', label: 'Cost of living', sign: -1 },
  { key: 'living', label: 'Living', sign: -1 },
  { key: 'tuition', label: 'Tuition', sign: -1 },
  { key: 'cards', label: 'Cards', sign: 0 },
];

function cell(x: number, sign: 1 | -1 | 0): string {
  if (x === 0) return '—';
  if (sign === 0) return moneyDelta(x);
  return `${sign < 0 ? '−' : '+'}${money(Math.abs(x))}`;
}

export function Ledger({ session, P }: { session: Session; P: Params }) {
  const life = lifetimeLedger(session.state);
  const year = thisYearLedger(session);
  const id = ledgerIdentity(session.state, P);
  return (
    <section className="panel ledger" aria-label="Ledger">
      <div className="panel-title">Ledger — every cent, itemised</div>
      <table>
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Ledger line</span>
            </th>
            <th scope="col">This year</th>
            <th scope="col">Lifetime</th>
          </tr>
        </thead>
        <tbody>
          <tr className="start-row">
            <th scope="row">Starting cash</th>
            <td />
            <td>+{money(P.S0)}</td>
          </tr>
          {ROWS.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              <td>{cell(year[r.key], r.sign)}</td>
              <td>{cell(life[r.key], r.sign)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">= Balance</th>
            <td />
            <td>{money(id.reproduced)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
